import React, { useState } from 'react';
import { Video, Camera, ArrowRight, Copy, Share2 } from 'lucide-react';
import VideoTestimonials from './VideoTestimonials';
import GuestMediaUpload from './GuestMediaUpload';
import { useParams } from 'react-router-dom';


const SubmitReview = () => {
  const [activeModal, setActiveModal] = useState(null);
  const { token } = useParams();
  const [copied, setCopied] = useState(false);

  const mediaUrl = `${window.location.origin}/media-gallery/${token || ''}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(mediaUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // fallback
      alert('Impossible de copier le lien automatiquement. Sélectionnez et copiez manuellement: ' + mediaUrl);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Médias de l'événement",
          text: "Consultez toutes les photos et vidéos de l'événement",
          url: mediaUrl,
        });
      } catch (err) {
        // user cancelled or error
      }
    } else {
      // fallback: copy to clipboard
      await handleCopy();
      alert('Partage non disponible sur ce navigateur. Le lien a été copié.');
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-[#f0f9ff]">
      {/* Conditionally render content based on activeModal */}
      {activeModal === null ? (
        // MAIN CONTENT
        <div className="container mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-center text-blue-600 mb-5">
              Partagez vos plus beaux souvenirs
            </h2>
            <p className="text-lg text-center text-gray-700 mb-4">
              Faites de ce moment un souvenir inoubliable en partageant vos photos, vidéos et mots doux.
            </p>
          </div>

          {/* Two Main Buttons */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">

            {/* Video Testimonial Button */}
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Video className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-extrabold text-center text-blue-600 mb-5">
                Votre Témoignage Vidéo
              </h3>
              <p className="text-gray-600 mb-6">
                Filmez-vous en partageant un message de vœux ou un souvenir
              </p>
              <ul className="text-sm text-gray-500 mb-6 space-y-1">
                <li>• Jusqu’à 1 minute d’enregistrement</li>
                <li>• Ajoutez vos informations pour qu’on puisse vous remercier</li>
                <li>• Partagez vos plus belles pensées</li>
              </ul>
              <button
                onClick={() => setActiveModal('testimonial')}
                className="justify-center items-center w-full h-[42px] bg-[#3686FF] text-white px-4 rounded-lg gap-2"
              >
                Filmez-vous
              </button>
            </div>

            {/* Guest Media Button */}
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Camera className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-extrabold text-center text-blue-600 mb-5">
                Chargez vos Photos & Vidéos
              </h3>
              <p className="text-gray-600 mb-6">
                Partagez vos photos et vidéos dans un album commun
              </p>
              <ul className="text-sm text-gray-500 mb-6 space-y-1">
                <li>• Ajoutez plusieurs fichiers à la fois</li>
                <li>• Photos et vidéos acceptées</li>
                <li>• Interface glisser-déposer</li>
              </ul>
              <button
                onClick={() => setActiveModal('media')}
                className="justify-center items-center w-full h-[42px] bg-[#3686FF] text-white px-4 rounded-lg gap-2"
              >
                Ajoutez vos médias
              </button>
            </div>
          </div>
          <div className="max-w-4xl mx-auto mt-8 shadow-lg ">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6">
              <div className="flex md:flex-row flex-col items-center justify-between gap-6">
                <div className="flex items-center gap-4 flex-1">
                  {/* Icon */}
                  <div className="bg-gradient-to-br from-[#3686FF] to-blue-600 rounded-lg w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3 14l4-4 3 3 4-5 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                    </svg>
                  </div>

                  {/* Text content */}
                  <div>
                    <h4 className="text-gray-900 font-semibold text-lg mb-0.5">
                      Accédez à tous les médias de l'événement
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Images and vidéos rassemblés pour votre événement
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <a
                    href={`/media-gallery/${token}`}
                    className="inline-flex items-center gap-2 bg-[#3686FF] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#2b6fd6] transition-colors duration-200 whitespace-nowrap"
                  >
                    Accéder aux médias
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <div className='flex items-center gap-3 mt-4'>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-150"
                  title={copied ? 'Copié' : 'Copier le lien'}
                >
                  <Copy className="w-4 h-4" />
                  <span className="text-sm">{copied ? 'Copié' : 'Copier'}</span>
                </button>

                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-150"
                  title="Partager"
                >
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm">Partager</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : activeModal === 'testimonial' ? (
        // TESTIMONIAL MODAL
        <VideoTestimonials onClose={() => setActiveModal(null)} />
      ) : activeModal === 'media' ? (
        // MEDIA UPLOAD MODAL
        <GuestMediaUpload onClose={() => setActiveModal(null)} />
      ) : null}
    </div>
  );
};

export default SubmitReview;
